/**
 * Dark-mode-aware Modal for mini-apps.
 *
 * This is a copy of web/app-home/src/components/ui/Modal.tsx with dark: variant
 * classes added throughout. Once the core Modal gains dark mode support upstream,
 * this file can be removed and the re-export in index.ts pointed back.
 */
import React, { useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';

type ModalProps = {
  open: boolean;
  title?: string;
  onClose: () => void;
  // Optional gate for close attempts (backdrop/esc/close-button).
  // Return false (or Promise<false>) to block closing.
  onRequestClose?: (reason: 'backdrop' | 'esc' | 'close-button') => boolean | Promise<boolean>;
  children: React.ReactNode;
  footer?: React.ReactNode;
  className?: string;
  // New options
  disableBackdropClose?: boolean;
  disableEscClose?: boolean;
  closeOnBackdrop?: boolean; // default true (unless disableBackdropClose)
  closeOnEsc?: boolean; // default true (unless disableEscClose)
  trapFocus?: boolean; // default true
  returnFocusOnClose?: boolean; // default true
  // Provide a custom header (replaces default title/close row)
  header?: React.ReactNode;
  // Hide the default header close button when using the default header
  hideCloseButton?: boolean;
  // Style customizations (global polished defaults with escape hatch)
  containerClassName?: string;
  headerClassName?: string;
  bodyClassName?: string;
  footerClassName?: string;
  // Switch to legacy spacing/tone if a caller needs it
  variant?: 'polished' | 'legacy';
};

export function Modal({
  open,
  title,
  onClose,
  onRequestClose,
  children,
  footer,
  className = '',
  disableBackdropClose = false,
  disableEscClose = false,
  closeOnBackdrop,
  closeOnEsc,
  trapFocus = true,
  returnFocusOnClose = true,
  header,
  hideCloseButton = false,
  containerClassName,
  headerClassName,
  bodyClassName,
  footerClassName,
  variant = 'polished',
}: ModalProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const prevActiveRef = useRef<HTMLElement | null>(null);

  const allowBackdrop = (closeOnBackdrop ?? true) && !disableBackdropClose;
  const allowEsc = (closeOnEsc ?? true) && !disableEscClose;

  const handleRequestClose = useCallback((reason: 'backdrop' | 'esc' | 'close-button') => {
    if ((reason === 'backdrop' && !allowBackdrop) || (reason === 'esc' && !allowEsc)) {
      return;
    }
    if (typeof onRequestClose === 'function') {
      const result = onRequestClose(reason);
      if (result !== null && typeof result === 'object' && typeof (result as any).then === 'function') {
        void (result).then((v) => { if (v !== false) onClose(); });
        return;
      }
      if (result === false) return;
    }
    onClose();
  }, [allowBackdrop, allowEsc, onRequestClose, onClose]);

  useEffect(() => {
    if (!open || !allowEsc) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleRequestClose('esc'); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, allowEsc, handleRequestClose]);

  useEffect(() => {
    if (!open) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Capture previously focused element and move focus inside
    if (returnFocusOnClose) {
      prevActiveRef.current = (document.activeElement as HTMLElement | null) || null;
    }
    if (trapFocus) {
      const node = containerRef.current;
      // Focus first focusable or container
      try {
        const focusables = node ? (node.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )) : null;
        const first = focusables && focusables.length ? focusables[0] : node;
        first?.focus();
      } catch {}
      const onKeyDown = (e: KeyboardEvent) => {
        if (e.key !== 'Tab') return;
        const n = containerRef.current;
        if (!n) return;
        const els = Array.from(n.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        )).filter((el) => !el.hasAttribute('disabled'));
        if (els.length === 0) return;
        const first = els[0];
        const last = els[els.length - 1];
        const cur = document.activeElement as HTMLElement | null;
        if (e.shiftKey) {
          if (cur === first || !n.contains(cur)) {
            last.focus();
            e.preventDefault();
          }
        } else {
          if (cur === last) {
            first.focus();
            e.preventDefault();
          }
        }
      };
      document.addEventListener('keydown', onKeyDown);
      return () => {
        document.body.style.overflow = prevOverflow;
        document.removeEventListener('keydown', onKeyDown);
        if (returnFocusOnClose && prevActiveRef.current) {
          try { prevActiveRef.current.focus(); } catch {}
          prevActiveRef.current = null;
        }
      };
    }
    return () => {
      document.body.style.overflow = prevOverflow;
      if (returnFocusOnClose && prevActiveRef.current) {
        try { prevActiveRef.current.focus(); } catch {}
        prevActiveRef.current = null;
      }
    };
  }, [open, trapFocus, returnFocusOnClose]);

  if (!open) return null;
  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      onClick={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
    >
      <div className="absolute inset-0 bg-black/30" data-testid="modal-backdrop" onClick={allowBackdrop ? () => handleRequestClose('backdrop') : undefined} />
      <div
        role="dialog"
        aria-modal="true"
        ref={containerRef}
        data-testid="modal-dialog"
        className={`relative z-10 bg-white dark:bg-nightshade-900 shadow-xl ${
        // Full-screen on mobile; on desktop clearly narrower than the 6xl layout to reveal context behind
        'w-full h-full rounded-none border-0 md:w-full md:h-auto md:max-h-[calc(100vh-96px)] md:rounded-lg md:border md:border-nightshade-200 dark:md:border-nightshade-700 md:max-w-[min(100vw-96px,67rem)]'
      } overflow-hidden flex flex-col ${className} ${containerClassName || ''}`}>
        {header ? (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3'} border-b border-nightshade-200 dark:border-nightshade-700 flex items-center justify-between ${headerClassName || ''}`}>
            {header}
          </div>
        ) : (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3'} border-b border-nightshade-200 dark:border-nightshade-700 flex items-center justify-between ${headerClassName || ''}`}>
            <h3 className="text-lg font-semibold text-nightshade-900 dark:text-nightshade-100">{title}</h3>
            {!hideCloseButton && (
              <button className="px-2 py-1 rounded border border-nightshade-300 dark:border-nightshade-600 text-nightshade-600 dark:text-nightshade-300 hover:bg-nightshade-50 dark:hover:bg-nightshade-800" onClick={() => handleRequestClose('close-button')} aria-label="Close" data-testid="modal-close">✕</button>
            )}
          </div>
        )}
        <div data-modal-body-scroll className={`flex-1 overflow-auto ${variant === 'legacy' ? 'p-4' : 'p-4'} ${bodyClassName || ''}`}>
          {children}
        </div>
        {footer && (
          <div className={`${variant === 'legacy' ? 'px-4 py-3' : 'px-4 py-3 bg-nightshade-50 dark:bg-nightshade-800'} border-t border-nightshade-200 dark:border-nightshade-700 flex items-center justify-end gap-2 ${footerClassName || ''}`}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
